import axios from 'axios';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || '';
const AUTH_BYPASS_PATHS = [
	'/auth/login',
	'/auth/register',
	'/auth/forgot-password',
	'/auth/reset-password',
	'/auth/refresh',
];

const api = axios.create({
	baseURL: API_BASE,
	withCredentials: true, // important for httpOnly cookies
	headers: {},
});

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
	accessToken = token;
}

export function getAccessToken() {
	return accessToken;
}

api.interceptors.request.use((config) => {
	if (accessToken && config.headers) {
		config.headers.Authorization = `Bearer ${accessToken}`;
	}
	return config;
});

let isRefreshing = false;
let failedQueue: Array<any> = [];

const processQueue = (
	error: any,
	token: string | null = null,
) => {
	failedQueue.forEach((prom) => {
		if (error) prom.reject(error);
		else prom.resolve(token);
	});
	failedQueue = [];
};

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const requestUrl = String(originalRequest?.url || '');
		const shouldBypassRefresh = AUTH_BYPASS_PATHS.some((path) =>
			requestUrl.includes(path),
		);

		if (
			error.response &&
			error.response.status === 401 &&
			originalRequest &&
			!originalRequest._retry &&
			!shouldBypassRefresh
		) {
			if (isRefreshing) {
				return new Promise(function (resolve, reject) {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers['Authorization'] =
							'Bearer ' + token;
						return api(originalRequest);
					})
					.catch((err) => Promise.reject(err));
			}
			originalRequest._retry = true;
			isRefreshing = true;
			try {
				const resp = await api.post('/auth/refresh');
				const newAccess = resp.data.accessToken;
				setAccessToken(newAccess);
				processQueue(null, newAccess);
				originalRequest.headers['Authorization'] =
					'Bearer ' + newAccess;
				return api(originalRequest);
			} catch (err) {
				processQueue(err, null);
				return Promise.reject(err);
			} finally {
				isRefreshing = false;
			}
		}
		return Promise.reject(error);
	},
);

export default api;
